import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwEventService,
  DwMetaDataFormApi,
  DwMetaDataFormSectionStateService,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwOrmDataService,
  DwSectionReadonlyBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { merge, Subject } from 'rxjs';
import { debounceTime, delay, takeUntil } from 'rxjs/operators';

import { AppMetaDataItemNames, DwUserEntity, StudentActivityEntity, StudentActivityTypeEntity } from '../../../../meta-data/app-meta-data.service';
import { DateTimeUtilService } from '../../../shared/util/date-time-util.service';
import { StudentRepositoryService } from '../../services/student-repository.service';

interface StudentActivityViewModel {
  id: number;
  title: string;
  detail: string;
  oldValueText: string;
  newValueText: string;
  actorDisplayName: string;
  actorRoleName: string;
  activityDateTime?: string | Date;
  activityDateTimeDisplay: string;
  iconClasses: string[];
  bodyClasses: string[];
  searchText: string;
}

@DwComponent({
  key: 'student-activity-form-section',
  name: 'Student Activity',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.DwUser,
  isGlobal: false,
})
@Component({
  selector: 'app-student-activity-form-section',
  templateUrl: './student-activity-form-section.component.html',
  styleUrls: ['./student-activity-form-section.component.scss']
})
export class StudentActivityFormSectionComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  formApi: DwMetaDataFormApi;
  activityItems: StudentActivityViewModel[] = [];
  filteredActivityItems: StudentActivityViewModel[] = [];
  searchTerm = '';
  loading = false;
  errorText = '';
  hasMore = false;
  totalRecordCount = 0;
  readonly skeletonRows = [1, 2, 3, 4];

  private readonly pageSize = 25;
  private readonly destroy$ = new Subject<void>();
  private readonly searchTermChanged$ = new Subject<string>();
  private activityRows: StudentActivityEntity[] = [];
  private currentStudentId?: number;
  private currentPageNo = 0;
  private serverSearchTerm = '';

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwFormSectionStateService: DwMetaDataFormSectionStateService,
    protected dwFormStateService: DwMetaDataFormStateService,
    private dwEventService: DwEventService,
    private studentRepositoryService: StudentRepositoryService,
    private readonly dateTimeUtilService: DateTimeUtilService
  ) {
    super(dwExpressionService, dwFormSectionStateService, dwFormStateService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.runAfterFormDataLoaded(() => this.refreshForCurrentStudent());

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.refreshForCurrentStudent());

    this.searchTermChanged$
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe(searchTerm => this.applySearchTerm(searchTerm));

    merge(...this.getActivityRefreshTopics().map(topic => this.dwEventService.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(topic))))
      .pipe(delay(300), takeUntil(this.destroy$))
      .subscribe(() => this.refreshForCurrentStudent(true));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    return;
  }

  onSearchTermChanged(searchTerm: string): void {
    this.searchTermChanged$.next(searchTerm || '');
  }

  loadNextPage(): void {
    if (!this.currentStudentId || this.loading || !this.hasMore) {
      return;
    }

    this.loadActivityPage(this.currentStudentId, this.currentPageNo + 1, false, this.serverSearchTerm);
  }

  trackByActivity(index: number, item: StudentActivityViewModel): number {
    return item.id;
  }

  private refreshForCurrentStudent(forceReload: boolean = false): void {
    const user = this.formApi.getFormData<DwUserEntity>();
    const studentId = user?.Student?.StudentId;

    if (studentId === this.currentStudentId && !forceReload) {
      return;
    }

    if (!studentId) {
      this.currentStudentId = undefined;
      this.currentPageNo = 0;
      this.totalRecordCount = 0;
      this.hasMore = false;
      this.activityRows = [];
      this.activityItems = [];
      this.applyActivityFilter();
      return;
    }

    this.currentStudentId = studentId;
    this.loadActivityPage(studentId, 1, true, this.serverSearchTerm);
  }

  private loadActivityPage(studentId: number, pageNo: number, reset: boolean, searchTerm: string = ''): void {
    this.loading = true;
    this.errorText = '';

    this.studentRepositoryService.getStudentActivityPage(studentId, pageNo, this.pageSize, searchTerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        const newRows = result?.Data || [];
        this.activityRows = reset ? newRows : [...this.activityRows, ...newRows];
        this.activityItems = this.activityRows.map((activity, index) => this.buildActivityViewModel(activity, index));
        this.totalRecordCount = result?.PageInfo?.TotalRecordCount || this.activityItems.length;
        this.hasMore = result?.PageInfo ? pageNo < result.PageInfo.TotalPageCount : newRows.length === this.pageSize;
        this.currentPageNo = pageNo;
        this.loading = false;
        this.applyActivityFilter();
      }, () => {
        this.loading = false;
        this.errorText = 'Activity could not be loaded.';
      });
  }

  private applySearchTerm(searchTerm: string): void {
    this.searchTerm = searchTerm || '';
    const normalizedSearchTerm = this.searchTerm.trim();

    if (!this.currentStudentId) {
      this.applyActivityFilter();
      return;
    }

    if (!normalizedSearchTerm) {
      if (this.serverSearchTerm) {
        this.serverSearchTerm = '';
        this.loadActivityPage(this.currentStudentId, 1, true);
        return;
      }

      this.applyActivityFilter();
      return;
    }

    if (this.hasLoadedAllActivityClientSide()) {
      this.serverSearchTerm = '';
      this.applyActivityFilter();
      return;
    }

    this.serverSearchTerm = normalizedSearchTerm;
    this.loadActivityPage(this.currentStudentId, 1, true, this.serverSearchTerm);
  }

  private applyActivityFilter(): void {
    const normalizedSearchTerm = this.searchTerm.trim().toLowerCase();

    this.filteredActivityItems = normalizedSearchTerm
      ? this.activityItems.filter(item => item.searchText.indexOf(normalizedSearchTerm) >= 0)
      : [...this.activityItems];
  }

  private hasLoadedAllActivityClientSide(): boolean {
    return !this.serverSearchTerm && !this.hasMore && this.activityItems.length >= this.totalRecordCount;
  }

  private getActivityRefreshTopics(): string[] {
    return [
      AppMetaDataItemNames.DwUser,
      AppMetaDataItemNames.Student,
      AppMetaDataItemNames.StudentActivity,
      AppMetaDataItemNames.StudentCourse,
      AppMetaDataItemNames.Assignment,
      AppMetaDataItemNames.AssignmentStudent,
      AppMetaDataItemNames.InstructorStudent,
      AppMetaDataItemNames.FlightSchoolGroupStudent,
      AppMetaDataItemNames.CourseEndorsementStudent,
      AppMetaDataItemNames.AcsWrittenResult
    ];
  }

  private buildActivityViewModel(activity: StudentActivityEntity, index: number): StudentActivityViewModel {
    const activityType = this.getActivityType(activity);
    const activityClass = activityType?.ActivityCssClass || this.getActivityClassFromTitle(activity.ActivityTitle);
    const title = activity.ActivityTitle || activityType?.ActivityTypeName || 'Activity';
    const detail = activity.ActivityDetail || '';
    const oldValueText = activity.OldValueText || '';
    const newValueText = activity.NewValueText || '';
    const actorDisplayName = activity.ActorDisplayName || 'System';
    const actorRoleName = activity.ActorRoleName || '';
    const iconModifierClass = this.getIconModifierClass(activityClass, activityType?.ActivityTypeKey, title);
    const iconActivityKeyClass = this.getActivityTypeKeyClass(activityType?.ActivityTypeKey);
    const searchText = [
      title,
      detail,
      oldValueText,
      newValueText,
      actorDisplayName,
      actorRoleName,
      activityType?.ActivityTypeName,
      activityType?.ActivityTypeKey
    ].filter(value => !!value).join(' ').toLowerCase();

    return {
      id: activity.StudentActivityId || index,
      title,
      detail,
      oldValueText,
      newValueText,
      actorDisplayName,
      actorRoleName,
      activityDateTime: activity.ActivityDateTime,
      activityDateTimeDisplay: activity.ActivityDateTime
        ? this.dateTimeUtilService.formatDateTimeToLocal(activity.ActivityDateTime)
        : '',
      iconClasses: ['audit-event__icon', iconModifierClass, activityClass, iconActivityKeyClass].filter(value => !!value),
      bodyClasses: ['audit-event__body', activityClass].filter(value => !!value),
      searchText
    };
  }

  private getActivityType(activity: StudentActivityEntity): StudentActivityTypeEntity | undefined {
    return activity.StudentActivityType;
  }

  private getActivityClassFromTitle(title?: string): string {
    const normalizedTitle = (title || '').toLowerCase();

    if (normalizedTitle.indexOf('course') >= 0) {
      return 'activity--course';
    }

    if (normalizedTitle.indexOf('instructor') >= 0) {
      return 'activity--instructor';
    }

    if (normalizedTitle.indexOf('group') >= 0) {
      return 'activity--group';
    }

    if (normalizedTitle.indexOf('school') >= 0) {
      return 'activity--school';
    }

    if (normalizedTitle.indexOf('assignment') >= 0) {
      return 'activity--assignment';
    }

    if (normalizedTitle.indexOf('endorse') >= 0) {
      return 'activity--endorsement';
    }

    if (normalizedTitle.indexOf('written') >= 0) {
      return 'activity--written';
    }

    return 'activity--system';
  }

  private getIconModifierClass(activityClass?: string, activityTypeKey?: string, title?: string): string {
    const value = `${activityClass || ''} ${activityTypeKey || ''} ${title || ''}`.toLowerCase();

    if (value.indexOf('instructor') >= 0) {
      return 'audit-event__icon--purple';
    }

    if (value.indexOf('assignment') >= 0 || value.indexOf('written') >= 0 || value.indexOf('removed') >= 0) {
      return 'audit-event__icon--warning';
    }

    if (value.indexOf('group') >= 0 || value.indexOf('endorse') >= 0 || value.indexOf('created') >= 0) {
      return 'audit-event__icon--success';
    }

    if (value.indexOf('profile') >= 0) {
      return 'audit-event__icon--muted';
    }

    return 'audit-event__icon--info';
  }

  private getActivityTypeKeyClass(activityTypeKey?: string): string {
    if (!activityTypeKey) {
      return '';
    }

    const normalizedKey = activityTypeKey
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .toLowerCase();

    return `activity-key--${normalizedKey}`;
  }
}
